/**
 * School page override system.
 *
 * What: Allows per-school content customization layered over generated variant content.
 * Why: High-priority schools benefit from more specific copy than the generic variant
 *      template provides. Override fields win; unspecified fields fall back to generated content.
 * When: Add an entry to SCHOOL_OVERRIDES for any school needing bespoke content.
 *       The slug must exactly match the slug in schoolData.ts.
 */

import type { SchoolPageContent } from './schoolPageContent.js';

/**
 * Partial override for a single school page.
 * All fields are optional — only specified fields replace the generated content.
 *
 * Array fields (supportBullets, faqItems, programFacts.facts, relatedLinks.links)
 * are APPENDED to the generated content by default.
 * Set _replaceArrays: true to replace arrays instead of appending.
 */
export interface SchoolPageOverride extends Partial<SchoolPageContent> {
  /**
   * When true, all array fields in this override replace the generated base arrays.
   * When false (default), array fields are appended to the generated base arrays.
   */
  _replaceArrays?: boolean;
}

/**
 * Merge an override into generated base content.
 *
 * Rules:
 * - Scalar fields: override value replaces base value.
 * - Array fields: appended to base arrays unless _replaceArrays is true.
 * - Nested objects (programFacts, relatedLinks): scalar sub-fields merged;
 *   array sub-fields follow the same _replaceArrays rule.
 * - Empty override (no fields): returns base unchanged.
 */
export function applySchoolOverride(
  base: SchoolPageContent,
  override: SchoolPageOverride
): SchoolPageContent {
  const replace = override._replaceArrays === true;

  // Strip internal flag before processing
  const { _replaceArrays: _, ...fields } = override;

  const merged: SchoolPageContent = { ...base };

  // Scalar top-level fields
  if (fields.metaTitle !== undefined) merged.metaTitle = fields.metaTitle;
  if (fields.metaDescription !== undefined)
    merged.metaDescription = fields.metaDescription;
  if (fields.heroHeading !== undefined) merged.heroHeading = fields.heroHeading;
  if (fields.heroSubheading !== undefined)
    merged.heroSubheading = fields.heroSubheading;
  if (fields.levelBadge !== undefined) merged.levelBadge = fields.levelBadge;
  if (fields.supportHeading !== undefined)
    merged.supportHeading = fields.supportHeading;

  // Array: supportBullets
  if (fields.supportBullets !== undefined) {
    merged.supportBullets = replace
      ? fields.supportBullets
      : [...base.supportBullets, ...fields.supportBullets];
  }

  // Array: faqItems
  if (fields.faqItems !== undefined) {
    merged.faqItems = replace
      ? fields.faqItems
      : [...base.faqItems, ...fields.faqItems];
  }

  // Nested: programFacts (scalar sub-fields + array facts)
  if (fields.programFacts !== undefined) {
    merged.programFacts = {
      ...base.programFacts,
      ...fields.programFacts,
      facts:
        fields.programFacts.facts !== undefined
          ? replace
            ? fields.programFacts.facts
            : [...base.programFacts.facts, ...fields.programFacts.facts]
          : base.programFacts.facts,
    };
  }

  // Nested: relatedLinks (scalar sub-fields + array links)
  if (fields.relatedLinks !== undefined) {
    merged.relatedLinks = {
      ...base.relatedLinks,
      ...fields.relatedLinks,
      links:
        fields.relatedLinks.links !== undefined
          ? replace
            ? fields.relatedLinks.links
            : [...base.relatedLinks.links, ...fields.relatedLinks.links]
          : base.relatedLinks.links,
    };
  }

  return merged;
}

/**
 * Per-school content overrides for high-priority schools.
 *
 * Coverage:
 * - raffles-institution          → ip variant
 * - raffles-girls-school-secondary → ip variant
 * - st-josephs-institution       → ip-ib variant
 * - anglo-chinese-school-independent → ip-ib variant
 * - school-of-the-arts-singapore → ip-ib variant
 * - nanyang-primary-school       → primary variant
 */
const SCHOOL_OVERRIDES: Record<string, SchoolPageOverride> = {
  'raffles-institution': {
    heroSubheading: `Dedicated IP Math classes for Raffles Institution students, covering the accelerated RI curriculum from Year 1 through Year 6 with A-Level preparation built in from Year 3.`,
    supportBullets: [
      `Teachers familiar with the RI internal assessment format and the expectations of RI Math teachers`,
    ],
    faqItems: [
      {
        question: `Does DeepThink teach the specific topics in the RI Year 1–6 Math curriculum?`,
        answer: `Yes. The RI IP curriculum moves faster and covers topics — including proof techniques, complex number foundations, and combinatorics — that do not appear in the standard secondary syllabus. Our IP class content is mapped to this accelerated track, not the O-Level syllabus.`,
      },
    ],
  },

  'raffles-girls-school-secondary': {
    heroSubheading: `IP Math tuition for Raffles Girls' School students — dedicated IP classes covering the Raffles Programme curriculum through to A-Level readiness.`,
    supportBullets: [
      `RGS students are on the Raffles Programme IP track — our IP class is built around this accelerated pace`,
    ],
    faqItems: [
      {
        question: `Is DeepThink familiar with the Raffles Programme Math curriculum at RGS?`,
        answer: `Yes. RGS students follow an accelerated IP track that bypasses O-Level examinations and progresses toward A-Levels. Our IP class content matches this pace, covering advanced topics that O-Level students do not encounter.`,
      },
    ],
  },

  'st-josephs-institution': {
    heroSubheading: `Dedicated IB Math classes for St. Joseph's Institution students, working on the IB Mathematics curriculum.`,
    supportBullets: [
      `SJI delivers the IB Diploma Programme — our dedicated IB class covers the IB Mathematics curriculum throughout secondary`,
    ],
    faqItems: [
      {
        question: `Does DeepThink have a class for SJI's IB curriculum?`,
        answer: `Yes. SJI students follow the IB Diploma Mathematics curriculum, and our dedicated IB class is built around it.`,
      },
    ],
  },

  'anglo-chinese-school-independent': {
    heroSubheading: `Dedicated IB Math classes for ACS(I) students, working on the IB Mathematics curriculum.`,
    supportBullets: [
      `ACS(I) follows the IB Diploma Programme — our dedicated IB class covers the IB Mathematics curriculum throughout secondary`,
    ],
    faqItems: [
      {
        question: `How does ACS(I)'s IB Math differ from the standard Singapore secondary Math syllabus?`,
        answer: `ACS(I) students sit the IB Diploma examinations, not O-Level or A-Level. The IB Mathematics syllabus is different in both content and structure from the MOE secondary curriculum, and our dedicated IB class is built around it.`,
      },
    ],
  },

  'school-of-the-arts-singapore': {
    heroSubheading: `Dedicated IB Math classes for School of the Arts (SOTA) students, working on the IB Mathematics curriculum alongside their arts specialisation.`,
    supportBullets: [
      `SOTA's six-year integrated programme leads to the IB Diploma or IB Career-related Programme — our dedicated IB class covers the IB Mathematics curriculum throughout`,
    ],
    faqItems: [
      {
        question: `Does DeepThink teach the IB Math curriculum that SOTA students follow?`,
        answer: `Yes. SOTA students work toward the IB Diploma (or IB Career-related Programme), and our dedicated IB Math class follows the IB Mathematics curriculum — not the local O-Level or A-Level syllabus.`,
      },
    ],
  },

  'nanyang-primary-school': {
    heroSubheading: `Primary Math tuition for Nanyang Primary School students — MOE syllabus-aligned, PSLE-focused, in classes capped at four students where every child receives individual attention.`,
    supportBullets: [
      `Nanyang Primary places strong emphasis on problem solving and mathematical reasoning — our P1–P6 curriculum reinforces the same methods your child learns in school`,
    ],
    faqItems: [
      {
        question: `Does DeepThink follow the same teaching approach as Nanyang Primary?`,
        answer: `We align to the MOE Primary Math syllabus that all Singapore primary schools including Nanyang use. The same model-drawing, heuristics, and problem-solving approaches your child learns in school are reinforced in our classes.`,
      },
    ],
  },
};

/**
 * Return the override for a given school slug, or undefined if none exists.
 */
export function getSchoolOverride(
  slug: string
): SchoolPageOverride | undefined {
  return SCHOOL_OVERRIDES[slug];
}

/**
 * Return all school slugs that have overrides defined.
 * Used for build-time validation and testing.
 */
export function listOverrideSlugs(): string[] {
  return Object.keys(SCHOOL_OVERRIDES);
}
